import { Suspense, ComponentType } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazy-with-retry";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";

import FramedSection from "@/components/FramedSection";
// HeroSummaryBand lazy-loaded below
// AnnouncementBar lazy-loaded below
// TestimonialsWall lazy-loaded below
// StatRowSection lazy-loaded below


const NotAnotherWrapper = lazy(() => import("@/components/NotAnotherWrapper"));
const HeroSummaryBand = lazy(() => import("@/components/HeroSummaryBand"));
const AnnouncementBar = lazy(() => import("@/components/AnnouncementBar"));
const TestimonialsWall = lazy(() => import("@/components/TestimonialsWall"));
const StatRowSection = lazy(() => import("@/components/StatRowSection"));

const Features = lazy(() => import("@/components/Features"));
const BrainMCP = lazy(() => import("@/components/BrainMCP"));
const TheStack = lazy(() => import("@/components/TheStack"));
const Compliance = lazy(() => import("@/components/Compliance"));
const WhyNow = lazy(() => import("@/components/WhyNow"));
const FinalCTA = lazy(() => import("@/components/FinalCTA"));
const Footer = lazy(() => import("@/components/Footer"));

/**
 * Tiny placeholder while a section's chunk streams in.
 * Reserves a small amount of vertical space so the page doesn't jump,
 * but stays invisible to keep the editorial feel.
 */
const SectionFallback = () => <div aria-hidden className="py-12" />;

/**
 * Each lazy section gets its OWN Suspense boundary so a slow or failed
 * chunk on mobile doesn't block the rest of the page from rendering.
 * Previously, a single shared Suspense meant one stuck chunk hid every
 * section below the hero indefinitely.
 */
const Section = ({ as: Cmp }: { as: ComponentType }) => (
  <Suspense fallback={<SectionFallback />}>
    <Cmp />
  </Suspense>
);

const Index = () => (
  <div className="min-h-screen bg-background text-foreground">
    <SEOHead
      title="Autonomous AI Agent Team Built on Claude Code | Mane"
      description="40+ specialist AI agents that research, code, write, find leads, and ship, running locally on your Claude Code. Works from Slack, Telegram, or your terminal."
      url="https://mane.dev"
      image="https://mane.dev/og.jpg"
      schema={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebSite",
            name: "Mane",
            url: "https://mane.dev",
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: "https://mane.dev/use-cases?q={search_term_string}",
              },
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@type": "SoftwareApplication",
            name: "Mane",
            url: "https://mane.dev",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web, macOS, Windows, Linux",
            description: "40+ specialist AI agents that research, code, write, find leads, and ship, running locally on your Claude Code.",
            offers: [
              { "@type": "Offer", price: "0", priceCurrency: "USD", name: "Free" },
              { "@type": "Offer", price: "20", priceCurrency: "USD", name: "Pro" },
            ],
            publisher: { "@type": "Organization", name: "Mane", url: "https://mane.dev" },
          },
        ],
      }}
    />
    <Navbar variant="overlay" />

    <Hero />

    <FramedSection noTop noBottom>
      <Section as={AnnouncementBar} />
    </FramedSection>

    <FramedSection pattern="noise">
      <Section as={NotAnotherWrapper} />
    </FramedSection>

    <FramedSection pattern="noise">
      <Section as={TheStack} />
    </FramedSection>

    <FramedSection pattern="grid">
      <Section as={Features} />
    </FramedSection>

    <FramedSection pattern="noise">
      <Section as={BrainMCP} />
    </FramedSection>

    <FramedSection pattern="noise">
      <Section as={Compliance} />
    </FramedSection>

    <FramedSection pattern="noise">
      <Section as={WhyNow} />
    </FramedSection>

    <FramedSection pattern="noise">
      <Section as={TestimonialsWall} />
    </FramedSection>

    <FramedSection pattern="noise">
      <Section as={StatRowSection} />
    </FramedSection>

    <FramedSection noBottom>
      <Section as={FinalCTA} />
    </FramedSection>

    <Section as={Footer} />
    
  </div>
);

export default Index;
